import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { ToeBoard } from "../../../../../http/border/types/toeBoard";
import { useToeBoardsApi } from "../../../../../http/border/useToeBoards";
import { AdminItem } from "../../../../../http/types/items";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateToeBoardModalDialogButton from "./CreateToeBoardModalDialogButton";
import ToeBoardForm from "./ToeBoardForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const ToeBoards: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "toeBoards" });
  const aborter = useRef(new AbortController());
  const toeBoardsApi = useToeBoardsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "toeBoards");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await toeBoardsApi.updateAdminToeBoardVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await toeBoardsApi.updateAdminToeBoardOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToEdit, setItemToEdit] = useState<ToeBoard>();
  const [itemToDelete, setItemToDelete] = useState<ToeBoard>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminItem>("border.toe_board");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = toeBoardsApi.useAdminToeBoards(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.toeBoards.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.toeBoards.search} value={searchTerm} onChange={handleSearchChange} />
        <CreateToeBoardModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<ToeBoard>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.items.singular}</th>
            <th align="left">{i18n.translation.common.length}</th>
            <th align="left">{i18n.translation.toeBoardExtras.plural}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.item.code}</td>
            <td>{item.length} mm</td>
            <td>{item.extras?.map((extra) => extra.item.name ?? extra.item.code).join("; ")}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item.item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.substructures.edit}>
          <ToeBoardForm onClose={() => setItemToEdit(undefined)} toeBoard={itemToEdit} />
        </ModalDialog>
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.toeBoards.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                toeBoardsApi
                  .deleteAdminToeBoard(itemToDelete)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.toeBoards.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default ToeBoards;
